import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { PermissionEnum } from '@Application/Security/Roles';
import MarkTranscriptReviewed from '@Application/Beneficiaries/Transcripts/Commands/MarkTranscriptReviewed';
import markTranscriptReviewed from '@Application/Beneficiaries/Transcripts/markTranscriptReviewed';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import TranscriptExportDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Dialogs/TranscriptExportDialog';
import { setSpeakerAssignmentContext } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type TranscriptSpeakersListView from '@UI/Store/Interviews/Views/TranscriptSpeakersListView';
import type TranscriptView from '@UI/Store/Interviews/Views/TranscriptView';
import { css } from '@emotion/react';
import { Check, Download, Users } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, Tag } from '@verclam/ui-kit';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import useTurnRevision from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/TurnRevisionProvider/useTurnRevision';

const timestampFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

const toolbarStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
`;

const headingStyle = css`
	display: flex;
	align-items: center;
	gap: 0.875rem;
	flex-wrap: wrap;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const countersStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	letter-spacing: 0.04em;
	color: ${theme.colors.inkMuted};
`;

const actionsStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly transcript: TranscriptView;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly speakers: TranscriptSpeakersListView | null;
	/** Transportés, jamais affichés : ce bloc écrit le signal du panneau d'attribution, qui les classe. */
	readonly participants: readonly InternalInterviewParticipantView[];
	/** `false` hides speaker attribution and the review action; export stays available. */
	readonly isWritable: boolean;
};

/**
 * Title, counters, review badge and the three actions of §9.4 — each wired differently on purpose:
 * "Attribuer" and "Exporter" only ever run through `runGuarded` (an open edit could otherwise be
 * lost by opening the sidebar or the export dialog), "Marquer comme relu" dispatches on its own,
 * untouched by that guard, since it never discards the open edit.
 */
const TranscriptReviewToolbar: React.FC<Props> = ({
	transcript,
	subject,
	interviewId,
	speakers,
	participants,
	isWritable,
}) => {
	const dispatch = useDispatch();
	const [isMarkingReviewed, setIsMarkingReviewed] = React.useState(false);
	const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false);

	const { runGuarded } = useTurnRevision();

	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const handleMarkReviewed = React.useCallback(() => {
		setIsMarkingReviewed(true);

		dispatch(markTranscriptReviewed(new MarkTranscriptReviewed(facilityId, subject, interviewId)))
			.catch(ignoreHandledError) // toast shown by InterviewsNotifications
			.finally(() => setIsMarkingReviewed(false));
	}, [dispatch, facilityId, subject, interviewId]);

	const handleAssignClick = React.useCallback(() => {
		const target = speakers?.list.find((speaker) => !speaker.isAssigned) ?? speakers?.list[0];

		if (target === undefined) {
			return;
		}

		runGuarded(() => {
			setSpeakerAssignmentContext({
				speakerId: target.id,
				subjectTypeKey: subject.typeKey,
				subjectId: subject.subjectId,
				interviewId,
				participants,
			});
		});
	}, [speakers, runGuarded, subject, interviewId, participants]);

	const handleExportClick = React.useCallback(() => {
		runGuarded(() => setIsExportDialogOpen(true));
	}, [runGuarded]);

	const handleHideExportDialog = React.useCallback(() => setIsExportDialogOpen(false), []);

	const hasAssignableSpeakers = speakers !== null && speakers.list.length > 0;

	return (
		<React.Fragment>
			<div css={toolbarStyle}>
				<div css={headingStyle}>
					<h2 css={titleStyle}>Transcript</h2>
					<span css={countersStyle}>
						{transcript.turnCount} tours · {transcript.revisedTurnCount} corrigés
					</span>
					<ConditionalLoader data={transcript.isReviewed && transcript.reviewedAt !== null}>
						<Tag
							value={`Relu le ${transcript.reviewedAt && timestampFormatter.format(transcript.reviewedAt)}`}
							severity="success"
						/>
					</ConditionalLoader>
				</div>
				<div css={actionsStyle}>
					<ConditionalLoader data={isWritable}>
						<WithPermission permission={PermissionEnum.INTERVIEW_WRITE}>
							<ConditionalLoader data={hasAssignableSpeakers}>
								<Button
									type="button"
									label="Attribuer les locuteurs"
									severity="secondary"
									outlined
									icon={<Users size={16} />}
									onClick={handleAssignClick}
								/>
							</ConditionalLoader>

							<ConditionalLoader data={!transcript.isReviewed}>
								<Button
									type="button"
									label="Marquer comme relu"
									severity="secondary"
									outlined
									icon={<Check size={16} />}
									loading={isMarkingReviewed}
									onClick={handleMarkReviewed}
								/>
							</ConditionalLoader>
						</WithPermission>
					</ConditionalLoader>
					<WithPermission permission={PermissionEnum.TRANSCRIPT_EXPORT}>
						<Button
							type="button"
							label="Exporter"
							icon={<Download size={16} />}
							onClick={handleExportClick}
						/>
					</WithPermission>
				</div>
			</div>
			<TranscriptExportDialog
				visible={isExportDialogOpen}
				facilityId={facilityId}
				subject={subject}
				interviewId={interviewId}
				isReviewed={transcript.isReviewed}
				hasSpeakers={hasAssignableSpeakers}
				onHide={handleHideExportDialog}
			/>
		</React.Fragment>
	);
};

export default TranscriptReviewToolbar;
